// src/features/admin/orders/ResultsTable.tsx
import React from "react";
import Button from "components/ui/Button";
import type { AdminOrderResultRowOut } from "api/adminResults";

function prettyRole(v: string | null): string {
  if (v === "branded") return "Branded";
  if (v === "original") return "Original";
  return "—";
}

function openUrl(r: AdminOrderResultRowOut): string | null {
  return r.file_url ? String(r.file_url) : null;
}

interface Props {
  results: AdminOrderResultRowOut[];
  selectedId?: number | null;

  onView: (r: AdminOrderResultRowOut) => void;
  onEdit: (r: AdminOrderResultRowOut) => void;
  onCopy: (r: AdminOrderResultRowOut) => void;
  onPublish: (r: AdminOrderResultRowOut) => void;
  onArchive: (r: AdminOrderResultRowOut) => void;
  onDelete: (r: AdminOrderResultRowOut) => void;
}

const ResultsTable: React.FC<Props> = ({
  results,
  selectedId,
  onView,
  onEdit,
  onCopy,
  onPublish,
  onArchive,
  onDelete
}) => {
  return (
    <div className="overflow-x-auto border rounded-2xl">
      {/* table-fixed enables reliable truncation */}
      <table className="min-w-full table-fixed text-sm">
        {/* Control column widths: Created + File thinner */}
        <colgroup>
          <col style={{ width: "170px" }} /> {/* Created */}
          <col style={{ width: "110px" }} /> {/* Status */}
          <col style={{ width: "110px" }} /> {/* Type */}
          <col style={{ width: "280px" }} /> {/* File */}
          <col /> {/* Actions (rest) */}
        </colgroup>

        <thead className="bg-gray-50">
          <tr>
            <th className="text-left p-3 whitespace-nowrap">Created</th>
            <th className="text-left p-3 whitespace-nowrap">Status</th>
            <th className="text-left p-3 whitespace-nowrap">Type</th>
            <th className="text-left p-3 whitespace-nowrap">File</th>
            <th className="text-left p-3 whitespace-nowrap">Actions</th>
          </tr>
        </thead>

        <tbody>
          {results.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center text-gray-500 p-3 italic">
                No results yet.
              </td>
            </tr>
          ) : (
            results.map((r) => {
              const isSelected = selectedId === r.id;
              const url = openUrl(r);
              const canEdit = r.file_role_code === "branded";

              return (
                <tr key={r.id} className={`border-t ${isSelected ? "bg-blue-50" : ""}`}>
                  {/* Created: narrower + no wrap */}
                  <td className="p-3 whitespace-nowrap">
                    {r.created_date ? new Date(r.created_date).toLocaleString() : "—"}
                  </td>

                  <td className="p-3 capitalize whitespace-nowrap">{r.status ?? "—"}</td>
                  <td className="p-3 whitespace-nowrap">{prettyRole(r.file_role_code)}</td>

                  {/* File: crop with ellipsis */}
                  <td className="p-3">
                    <div className="min-w-0">
                      <span className="block truncate" title={r.file_name || ""}>
                        {r.file_name || "—"}
                      </span>
                    </div>
                  </td>

                  <td className="p-3">
                    <div className="flex gap-2 flex-wrap">
                      <Button type="button" variant="secondary" onClick={() => onView(r)}>
                        View
                      </Button>

                      <Button type="button" variant="secondary" onClick={() => onCopy(r)}>
                        Copy
                      </Button>

                      {canEdit && (
                        <Button type="button" variant="secondary" onClick={() => onEdit(r)}>
                          Edit
                        </Button>
                      )}

                      {url && (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="py-2 px-4 font-semibold rounded focus:outline-none focus:ring bg-gray-200 hover:bg-gray-300 text-gray-800"
                        >
                          Open
                        </a>
                      )}

                      {r.status === "draft" && (
                        <Button type="button" onClick={() => onPublish(r)}>
                          Publish
                        </Button>
                      )}

                      {r.status === "published" && (
                        <Button type="button" variant="secondary" onClick={() => onArchive(r)}>
                          Archive
                        </Button>
                      )}

                      <Button type="button" variant="danger" onClick={() => onDelete(r)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ResultsTable;
